import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Upload, Warning } from '@mui/icons-material';
import {
  Button,
  FormHelperText,
  InputLabel,
  Stack,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  UserProjectSubscription,
  UserProjectTemplate,
  resetDraftState,
  selectCurrentBoundary,
  selectCurrentHashId,
  selectCurrentProjectEnd,
  selectCurrentProjectName,
  selectSpecialEventDates,
  setDraftBoundary,
  setDraftProjectEnd,
  setDraftProjectName,
  useProjectBoundaryProcessMutation,
  useUserProjectRequestCreateMutation,
  useUserProjectRequestUpdateMutation,
} from 'state/userAdminSlices';

import { MAX_CHARS } from 'appConstants';
import { SOMETHING_WENT_WRONG_SAVING_MSG, getSavedSuccessMsg } from 'appUtils';
import dayjs from 'dayjs';
import { SecondaryText } from 'features/common/SecondaryText';
import { useSubscription } from 'features/common/hooks/useSubscription';
import { showToast } from 'features/common/toasts/ToastProvider';
import { classNames } from 'features/common/utils';
import { MapContext } from 'features/map/MapContext';
import { ProjectBoundaryEditorMap } from 'features/user_admin/ProjectBoundaryEditorMap';
import {
  formatAreaMi2,
  projectUrl,
  subscriptionHomeUrl,
} from 'features/user_admin/common';

import { ProjectTabs } from './ProjectTabs';
import SpecialDateRequestPanel from './SpecialDateRequest/SpecialDateRequestPanel';
import { useDatesQuota } from './SpecialDateRequest/hooks';

import date_picker_styles from '../task_bar/DatePicker.module.css';
import styles from './ProjectDetailCommon.module.css';
import common_styles from './UserProjectsPanel.module.css';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export function UserProjectRequestEdit({
  template,
  subscription,
}: {
  template: UserProjectTemplate | undefined;
  subscription: UserProjectSubscription;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const projectName = useAppSelector(selectCurrentProjectName);
  const boundary = useAppSelector(selectCurrentBoundary);
  const hash_id = useAppSelector(selectCurrentHashId);
  const projectEnd = useAppSelector(selectCurrentProjectEnd);
  const uprDates = useAppSelector(selectSpecialEventDates);
  const { project_data } = useSubscription();
  const specialDates = project_data?.special_date_requests ?? uprDates;
  const { mapReady } = useContext(MapContext);
  const [projectNameError, setProjectNameError] = useState<string>(undefined);
  const [projectDateError, setProjectDateError] = useState<string>(undefined);
  const [boundaryError, setBoundaryError] = useState<string>(undefined);
  const [processedBoundaryErrorMessage, setProcessedBoundaryError] =
    useState<string[]>(undefined);
  const [savedSnackOpen, setSavedSnackOpen] = useState(false);

  const today = dayjs();

  const [
    processProjectBoundary,
    { data: proccessedBoundaryInfo, error: processedBoundaryError },
  ] = useProjectBoundaryProcessMutation();

  const [
    createProjectRequest,
    { data: createProjectRequestData, isLoading: createProjectRequestLoading },
  ] = useUserProjectRequestCreateMutation();
  const [updateProjectRequest, { isLoading: updateProjectRequestLoading }] =
    useUserProjectRequestUpdateMutation();

  const { subscription_id } = subscription;
  const existing_project_names = useMemo(() => {
    return [
      ...subscription.projects.map((p) => p.project_name),
      ...subscription.project_requests.map((p) =>
        p.hash_id !== hash_id ? p.project_name : '',
      ),
    ];
  }, [subscription, hash_id]);

  const setBoundary = (value) => {
    dispatch(setDraftBoundary({ subscription_id, boundary: value }));
  };

  const setProjectName = (value) => {
    if (value?.length > MAX_CHARS)
      setProjectNameError(`Maximum ${MAX_CHARS} characters`);
    if (value && existing_project_names.includes(value)) {
      setProjectNameError(
        'A project of this name already exists in your subscription',
      );
    } else {
      setProjectNameError(undefined);
    }
    dispatch(setDraftProjectName({ subscription_id, project_name: value }));
  };

  const setProjectDate = (value) => {
    if (!value) {
      setProjectDateError('End date must be set');
    } else if (value > dayjs(subscription.template.end_date)) {
      setProjectDateError(
        `End date must be before ${subscription.template.end_date}`,
      );
    } else {
      setProjectDateError(undefined);
    }
    dispatch(
      setDraftProjectEnd({
        subscription_id,
        project_end: value && value.format('YYYY-MM-DD'),
      }),
    );
  };
  const { allQuota, usedQuota } = useDatesQuota();
  const quotaExceeded = usedQuota > allQuota;

  const cannotSave =
    !projectName ||
    !boundary ||
    !!projectNameError ||
    createProjectRequestLoading ||
    updateProjectRequestLoading ||
    quotaExceeded;
  const cannotSubmit =
    cannotSave ||
    !!boundaryError ||
    !subscription.can_create ||
    !!projectDateError ||
    quotaExceeded;

  useEffect(() => {
    if (!projectEnd) {
      setProjectDate(dayjs(subscription.template.end_date));
    }
  }, [subscription]);

  useEffect(() => {
    if (
      // If we've created a UPR with a new hash_id, we have to wait for the subscription data to update before we can navigate to that page or it is sad
      createProjectRequestData?.hash_id &&
      createProjectRequestData.hash_id !== hash_id &&
      subscription.project_requests.filter(
        (item) => item.hash_id === createProjectRequestData.hash_id,
      ).length > 0
    ) {
      navigate(projectUrl(subscription_id, createProjectRequestData));
    }
  }, [createProjectRequestData, subscription]);

  const createOrUpdateUpr = (is_draft) => {
    let prom;
    if (hash_id) {
      prom = updateProjectRequest({
        project_name: projectName,
        boundary,
        subscription_id,
        is_draft,
        hash_id,
        project_end: projectEnd,
        special_date_requests: specialDates,
      });
    } else {
      prom = createProjectRequest({
        project_name: projectName,
        boundary,
        subscription_id,
        is_draft,
        project_end: projectEnd,
        special_date_requests: specialDates,
      });
    }
    prom
      .unwrap()
      .then(() => {
        setSavedSnackOpen(true);
        navigate(subscriptionHomeUrl(subscription_id));
        showToast('success', getSavedSuccessMsg('project'));
      })
      .catch((rejected) => {
        showToast('error', SOMETHING_WENT_WRONG_SAVING_MSG);
        console.error(rejected);
      });
  };

  useEffect(() => {
    if (proccessedBoundaryInfo) {
      const newBoundary =
        proccessedBoundaryInfo.boundary_geojson.features[0].geometry;
      setBoundary(newBoundary);
      setProcessedBoundaryError(proccessedBoundaryInfo.warnings);
    }
  }, [proccessedBoundaryInfo, mapReady]);

  useEffect(() => {
    if (processedBoundaryError) {
      setProcessedBoundaryError([
        'Processing boundary failed for unknown reason, please try with a different file',
      ]);
    }
  }, [processedBoundaryError]);

  return (
    <Stack
      sx={{ height: '100%' }}
      alignItems="left"
      className={common_styles.content_block}
    >
      <Stack className={styles.project_request_header}>
        <Typography variant="h1">New Project Details</Typography>
        <Typography>
          This project will be added to the {subscription.subscription_name}{' '}
          subscription
        </Typography>
      </Stack>
      <Stack
        direction="column"
        justifyContent="center"
        alignContent="baseline"
        gap="0.5em"
      >
        <Stack direction="row">
          <InputLabel className={styles.project_input_label} id="project-name">
            Project Name:
          </InputLabel>
          <TextField
            value={projectName || ''}
            onChange={(ev) => setProjectName(ev.target.value)}
            error={!!projectNameError}
            helperText={projectNameError}
            aria-labelledby="project-name"
            size="small"
            variant="outlined"
            required
            inputProps={{ maxLength: MAX_CHARS }}
            className={styles.project_name_input}
          />
        </Stack>
        <Stack direction="row">
          <InputLabel className={styles.project_input_label} id="project-end">
            Project end date:
          </InputLabel>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
            <Stack sx={{ width: '100%' }}>
              <DatePicker
                className={classNames(
                  date_picker_styles.task_bar_date_picker_upr,
                  projectDateError
                    ? styles.project_date_picker_error
                    : styles.project_date_picker,
                )}
                value={projectEnd ? dayjs(projectEnd) : null}
                minDate={today}
                maxDate={dayjs(subscription?.template?.end_date)}
                reduceAnimations
                onChange={setProjectDate}
              />
              {projectDateError && (
                <FormHelperText error>{projectDateError}</FormHelperText>
              )}
            </Stack>
          </LocalizationProvider>
        </Stack>
      </Stack>
      <ProjectTabs>
        <Stack sx={{ height: '100%' }}>
          <Stack direction="row" justifyContent="space-between">
            <Stack>
              <Typography>Set Project Boundary</Typography>
              <SecondaryText>
                Keep project boundary within your subscription limits and the
                area below{' '}
                {formatAreaMi2(
                  subscription.template.project_max_area_mi2,
                  subscription.template.uses_metric,
                )}
              </SecondaryText>
            </Stack>
            <Button startIcon={<Upload />} component="label">
              Upload Boundary
              <VisuallyHiddenInput
                type="file"
                accept=".json,.geojson,.shp,.zip,.kml,.csv,.gpkg"
                onChange={(event) => {
                  processProjectBoundary({ file: event.target.files[0] });
                }}
              />
            </Button>
          </Stack>
          <ProjectBoundaryEditorMap
            boundary={boundary}
            setBoundary={setBoundary}
            subscription={subscription}
            project_max_area_mi2={template?.project_max_area_mi2}
            boundaryError={boundaryError}
            setBoundaryError={setBoundaryError}
          />
          {processedBoundaryErrorMessage &&
            processedBoundaryErrorMessage.map((message) => (
              <Stack
                key={message}
                alignItems="center"
                direction="row"
                gap={1}
                justifyContent="center"
              >
                <Warning />
                <Typography>{message}</Typography>
              </Stack>
            ))}
          <Stack
            direction="row"
            sx={{
              justifyContent: 'end',
              alignItems: 'space-between',
            }}
          />
        </Stack>
        <SpecialDateRequestPanel />
      </ProjectTabs>
      <Stack direction="row" justifyContent="space-between">
        <Button
          variant="outlined"
          onClick={() => {
            dispatch(resetDraftState());
            navigate(subscriptionHomeUrl(subscription_id));
          }}
        >
          Cancel
        </Button>
        <Stack
          direction="row"
          gap="1em"
          sx={{
            justifyContent: 'end',
            alignItems: 'end',
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              createOrUpdateUpr(true);
            }}
            disabled={cannotSave}
          >
            Save as draft
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              createOrUpdateUpr(false);
            }}
            disabled={cannotSubmit}
          >
            Submit
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
